import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';

// Renders model-generated markdown (bold, lists, links, inline code) without
// imposing font sizes - text size/color come from the wrapping element so each
// card keeps its design-spec typography.
const blockComponents: Components = {
    p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
    strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
    a: ({ href, children }) => (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
        >
            {children}
        </a>
    ),
    ul: ({ children }) => <ul className="list-disc pl-5 mb-2 last:mb-0">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal pl-5 mb-2 last:mb-0">{children}</ol>,
    li: ({ children }) => <li className="mb-1 last:mb-0">{children}</li>,
    code: ({ children }) => (
        <code className="font-mono text-[0.9em] bg-gray-100 px-1 py-0.5 rounded">{children}</code>
    ),
    h1: ({ children }) => (
        <div className="font-bold text-gray-900 mt-3 mb-1 first:mt-0">{children}</div>
    ),
    h2: ({ children }) => (
        <div className="font-bold text-gray-900 mt-3 mb-1 first:mt-0">{children}</div>
    ),
    h3: ({ children }) => (
        <div className="font-semibold text-gray-900 mt-3 mb-1 first:mt-0">{children}</div>
    ),
    h4: ({ children }) => (
        <div className="font-semibold text-gray-900 mt-3 mb-1 first:mt-0">{children}</div>
    ),
    blockquote: ({ children }) => (
        <blockquote className="border-l-2 border-gray-200 pl-3 mb-2 last:mb-0">
            {children}
        </blockquote>
    )
};

// For short strings inside flex rows (recap bullets, action items): paragraphs
// collapse to spans so the text flows inline with its marker.
const inlineComponents: Components = {
    ...blockComponents,
    p: ({ children }) => <span>{children}</span>
};

interface MarkdownTextProps {
    text: string;
    inline?: boolean;
}

function MarkdownText({ text, inline = false }: MarkdownTextProps) {
    return (
        <ReactMarkdown components={inline ? inlineComponents : blockComponents}>
            {text}
        </ReactMarkdown>
    );
}

export { MarkdownText };
